// SCR-STAFF-414 — گزارش درآمد پیش‌دریافت (US-BIL-152): مانده پکیج و عضویت در تاریخ گزارش به تفکیک خدمت و ماه انقضا، کیف پول نقدی و هدیه، گردش
// بازه (فروش، مصرف، انقضا، لغو، استرداد) و تطبیق مانده پکیج با حساب DeferredRevenue.Package حسابداری (C-AGENT-830).
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { unwrap } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, JalaliDatePickerRHF, KeyValueList, ListPage, LoadingState, toBusinessDate } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Amount, bilKeys, num, SCR_STAFF_414 } from './common';

export function DeferredRevenuePage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const today = toBusinessDate(new Date());
  const { control, watch } = useForm<{ from: string | null; to: string | null }>({ defaultValues: { from: today, to: today } });
  const from = watch('from') ?? today;
  const to = watch('to') ?? from;
  const report = useQuery({
    queryKey: bilKeys.deferred(session?.tenantId, `${from}|${to}`),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/reports/deferred-revenue', { params: { query: { asOf: to, from, to } } })),
  });
  const data = report.data;
  return (
    <ListPage
      screenId={SCR_STAFF_414}
      titleKey="bil:deferred.title"
      subtitleKey="bil:deferred.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <JalaliDatePickerRHF control={control} name="from" labelKey="bil:report.from" />
          <JalaliDatePickerRHF control={control} name="to" labelKey="bil:deferred.as_of" />
        </Stack>
      }
    >
      {report.isPending ? <LoadingState rows={4} /> : null}
      {report.error ? <ErrorState error={report.error} onRetry={() => void report.refetch()} /> : null}
      {data ? (
        <Stack spacing={2} data-testid="bil-deferred-report">
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('bil:deferred.balances')} <JalaliDate value={data.asOf} />
            </Typography>
            <KeyValueList
              columns={3}
              items={[
                { labelKey: 'bil:deferred.package', value: <Amount money={data.packageBalance} testId="bil-deferred-package" /> },
                { labelKey: 'bil:deferred.membership', value: <Amount money={data.membershipBalance} testId="bil-deferred-membership" /> },
                { labelKey: 'bil:finance.wallet_cash', value: <Amount money={data.walletCash} /> },
                { labelKey: 'bil:finance.wallet_gift', value: <Amount money={data.walletGift} /> },
                { labelKey: 'bil:deferred.total', value: <Amount money={data.total} testId="bil-deferred-total" /> },
                { labelKey: 'bil:deferred.accounting', value: <Amount money={data.accountingPackageBalance} testId="bil-deferred-accounting" /> },
              ]}
            />
            <Alert severity={data.reconciled ? 'success' : 'warning'} sx={{ mt: 1 }} data-testid="bil-deferred-reconciled" data-reconciled={String(data.reconciled)}>
              {data.reconciled ? t('bil:deferred.reconciled') : t('bil:deferred.not_reconciled')}
            </Alert>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('bil:deferred.movement')}
            </Typography>
            <KeyValueList
              columns={3}
              items={[
                { labelKey: 'bil:deferred.sold', value: <Amount money={data.movement.sold} testId="bil-deferred-sold" /> },
                { labelKey: 'bil:deferred.consumed', value: <Amount money={data.movement.consumed} testId="bil-deferred-consumed" /> },
                { labelKey: 'bil:deferred.expired', value: <Amount money={data.movement.expired} /> },
                { labelKey: 'bil:deferred.cancelled', value: <Amount money={data.movement.cancelled} /> },
                { labelKey: 'bil:deferred.refunded', value: <Amount money={data.movement.refunded} /> },
              ]}
            />
          </Paper>
          <Breakdown
            title={t('bil:deferred.by_service')}
            head={t('bil:packages.service')}
            rows={data.byService.map((row) => ({ key: row.serviceId ?? 'flex', label: row.serviceName ?? t('bil:packages.flexible'), sessions: num(row.sessions), value: row.value }))}
          />
          <Breakdown
            title={t('bil:deferred.by_expiry')}
            head={t('bil:deferred.expiry_month')}
            rows={data.byExpiry.map((row) => ({ key: row.expiresMonth, label: <JalaliDate value={row.expiresMonth} />, sessions: num(row.sessions), value: row.value }))}
          />
        </Stack>
      ) : null}
    </ListPage>
  );
}

interface BreakdownRow {
  key: string;
  label: React.ReactNode;
  sessions: number;
  value: { amount: number; currency: string };
}

function Breakdown({ title, head, rows }: { title: string; head: string; rows: BreakdownRow[] }) {
  const { t } = useTranslation('bil');
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography color="text.secondary">{t('bil:finance.none')}</Typography>
      ) : (
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>{head}</TableCell>
              <TableCell>{t('bil:deferred.sessions')}</TableCell>
              <TableCell>{t('bil:deferred.value')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key} data-testid="bil-deferred-row">
                <TableCell>{row.label}</TableCell>
                <TableCell>{faDigits(row.sessions)}</TableCell>
                <TableCell>
                  <Amount money={row.value} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
